import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Avatar,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack,
  PhotoCamera,
  Save,
  Cancel,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';
import { validateCustomerForm, getValidationError, CustomerFormData } from '../../utils/validation';

const AddCustomer: React.FC = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [errors, setErrors] = useState<any>({});
  const [formData, setFormData] = useState({
    fullName: '',
    customerType: 'active',
    email: '',
    phone: '',
    companyName: '',
    contactPerson: '',
    streetArea: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    creditLimit: '',
    currentBalance: '',
    notes: '',
    package: '',
  });

  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        const response = await apiService.getSubscriptions();
        setSubscriptions(response.data || []);
      } catch (error) {
        console.error('Failed to fetch subscriptions:', error);
      }
    };
    fetchSubscriptions();
  }, []);

  const handleInputChange = (field: string) => (event: any) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: ''
      });
    }
  };

  const validateForm = (): boolean => {
    const validationErrors = validateCustomerForm(formData as CustomerFormData);
    
    if (validationErrors) {
      setErrors(validationErrors);
      return false;
    }
    
    setErrors({});
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form using validate.js
    const validationErrors = validateCustomerForm(formData as CustomerFormData);
    
    console.log('=== VALIDATION DEBUG ===');
    console.log('Form data:', formData);
    console.log('Validation errors:', validationErrors);
    console.log('Errors state before:', errors);
    
    if (validationErrors) {
      setErrors(validationErrors);
      console.log('Errors state after:', validationErrors);
      toast.error('Please fix the validation errors');
      return;
    }
    
    setErrors({});
    
    try {
      const response = await apiService.createCustomer({
        name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        status: formData.customerType || 'active',
        company_name: formData.companyName,
        contact_person: formData.contactPerson,
        address: formData.streetArea,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        postal_code: formData.postalCode,
        credit_limit: formData.creditLimit ? parseFloat(formData.creditLimit) : null,
        current_balance: formData.currentBalance ? parseFloat(formData.currentBalance) : null,
        notes: formData.notes,
      });
      
      console.log('Create response:', response);
      
      // If a subscription is selected, assign it to the customer
      if (formData.package && response.data?.id) {
        const subscriptionId = parseInt(formData.package);
        const selectedSubscription = subscriptions.find(sub => sub.id === subscriptionId);
        
        if (selectedSubscription) {
          await apiService.assignSubscriptionsToCustomer(response.data.id, [
            {
              subscription_id: subscriptionId,
              status: 'active',
              start_date: new Date().toISOString().split('T')[0],
            }
          ]);
        }
      }
      
      if (response.success) {
        toast.success('Customer created successfully');
    navigate('/customers');
      } else {
        toast.error(response.message || 'Failed to create customer');
      }
    } catch (error: any) {
      console.error('Failed to create customer:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to create customer');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Add a New Customer
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Avatar
                    sx={{
                      width: 120,
                      height: 120,
                      mb: 2,
                      backgroundColor: 'grey.300',
                    }}
                  >
                    <PhotoCamera sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Button
                    variant="contained"
                    startIcon={<PhotoCamera />}
                    sx={{ mb: 2 }}
                  >
                    Upload
                  </Button>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Allowed format JPG, JPEG, and PNG
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Max file size 2MB
                  </Typography>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 8 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  Contact Information
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      placeholder="Enter"
                      value={formData.fullName}
                      onChange={handleInputChange('fullName')}
                      error={!!errors.fullName}
                      helperText={getValidationError('fullName', errors)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth error={!!errors.customerType}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={formData.customerType}
                        label="Status"
                        onChange={handleInputChange('customerType')}
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                        <MenuItem value="suspended">Suspended</MenuItem>
                      </Select>
                      {errors.customerType && (
                        <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                          {getValidationError('customerType', errors)}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      placeholder="Enter"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange('email')}
                      error={!!errors.email}
                      helperText={getValidationError('email', errors)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      placeholder="Enter"
                      value={formData.phone}
                      onChange={handleInputChange('phone')}
                      error={!!errors.phone}
                      helperText={getValidationError('phone', errors)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Notes"
                      multiline
                      rows={3}
                      placeholder="Enter"
                      value={formData.notes}
                      onChange={handleInputChange('notes')}
                    />
                  </Grid>
                </Grid>

                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', mt: 3 }}>
                  Company Information
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Company Name"
                      placeholder="Enter"
                      value={formData.companyName}
                      onChange={handleInputChange('companyName')}
                      error={!!errors.companyName}
                      helperText={getValidationError('companyName', errors)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Contact Person"
                      placeholder="Enter"
                      value={formData.contactPerson}
                      onChange={handleInputChange('contactPerson')}
                      error={!!errors.contactPerson}
                      helperText={getValidationError('contactPerson', errors)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Credit Limit"
                      type="number"
                      placeholder="Enter"
                      value={formData.creditLimit}
                      onChange={handleInputChange('creditLimit')}
                      error={!!errors.creditLimit}
                      helperText={getValidationError('creditLimit', errors)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Current Balance"
                      type="number"
                      placeholder="Enter"
                      value={formData.currentBalance}
                      onChange={handleInputChange('currentBalance')}
                      error={!!errors.currentBalance}
                      helperText={getValidationError('currentBalance', errors)}
                    />
                  </Grid>
                </Grid>

                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  Address
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Address"
                      placeholder="Enter"
                      value={formData.streetArea}
                      onChange={handleInputChange('streetArea')}
                      error={!!errors.streetArea}
                      helperText={getValidationError('streetArea', errors)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="City"
                      placeholder="Enter"
                      value={formData.city}
                      onChange={handleInputChange('city')}
                      error={!!errors.city}
                      helperText={getValidationError('city', errors)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="State"
                      placeholder="Enter"
                      value={formData.state}
                      onChange={handleInputChange('state')}
                      error={!!errors.state}
                      helperText={getValidationError('state', errors)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Country"
                      placeholder="Enter"
                      value={formData.country}
                      onChange={handleInputChange('country')}
                      error={!!errors.country}
                      helperText={getValidationError('country', errors)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Postal Code"
                      placeholder="Enter"
                      value={formData.postalCode}
                      onChange={handleInputChange('postalCode')}
                      error={!!errors.postalCode}
                      helperText={getValidationError('postalCode', errors)}
                    />
                  </Grid>
                </Grid>

                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  Subscription
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <FormControl fullWidth>
                      <InputLabel>Select Subscription</InputLabel>
                      <Select
                        value={formData.package}
                        label="Select Subscription"
                        onChange={handleInputChange('package')}
                      >
                        <MenuItem value="">None</MenuItem>
                        {subscriptions.map((sub) => (
                          <MenuItem key={sub.id} value={sub.id.toString()}>
                            {sub.name} - {sub.subscription_number}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
              <Button
                variant="outlined"
                startIcon={<Cancel />}
                onClick={() => navigate('/customers')}
                sx={{ px: 4 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<Save />}
                sx={{ px: 4 }}
              >
                Add Customer
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AddCustomer;
